import TracPage from '@services/tracpage/types/trac-page';
import React, { FC, useMemo } from 'react';
import { HomeWrapperItems } from '@shared-tracpage/components/home-wrapper-items';
import { TourItem } from '@shared-tracpage/components/tour-item';
import { ContentGrid } from '@shared-tracpage/components/content-grid';
import { List } from '@shared-tracpage/components/list';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import { TourEvent } from '@common/types/traclink/tour-event';
import { SectionIdentifier } from '@services/tracpage/types/trac-page-section-id';
import { SongLink } from '@shared-tracpage/components/song-link';
import { MerchProduct } from '@services/merch/types/api/merch-product';
import { ProductItemAlt } from '@shared-tracpage/components/product-item-alt';
import {
  groupEventsByVisibility,
  groupMerchByVisibility,
  groupMusicByVisibility,
} from '@shared-tracpage/utils/group-by-visibility';
import { getMainPageSortFn } from '@shared-tracpage/utils/items-sort';
import { getSectionsToRender } from '@shared-tracpage/utils/sections-render';
import { TracPageLink } from '@services/tracpage/types/trac-page-link';

const MAX_ITEMS = 3;

type StateProps = {
  pageData: TracPage;
  events: TourEvent[];
  merch: MerchProduct[];
  dspsMap: Record<string, TracPageLink[]>;
};

type Props = StateProps;

const mapStateToProps = (state: AppState): StateProps => ({
  pageData: state.tracpage.artistPageDraft,
  events: [
    ...state.traclink.tourEventDrafts.new,
    ...state.traclink.tourEvents.map(
      (te) => state.traclink.tourEventDrafts.edit[te.id] ?? te
    ),
  ],
  merch: state.merch.artistMerch,
  dspsMap: state.tracpage.dspsMap,
});

const AllPage: FC<Props> = ({ pageData, events, merch, dspsMap }) => {
  const releases = useMemo(() => {
    return groupMusicByVisibility(pageData)
      .visible.slice(0, MAX_ITEMS)
      .sort(getMainPageSortFn(pageData, SectionIdentifier.MUSIC));
  }, [pageData]);

  const products = useMemo(() => {
    return groupMerchByVisibility(merch, pageData)
      .visible.slice(0, MAX_ITEMS)
      .sort(getMainPageSortFn(pageData, SectionIdentifier.MERCH));
  }, [merch, pageData]);

  const tourEvents = useMemo(() => {
    return groupEventsByVisibility(events, pageData)
      .visible.slice(0, MAX_ITEMS)
      .sort(getMainPageSortFn(pageData, SectionIdentifier.EVENTS));
  }, [events, pageData]);

  return (
    <>
      <HomeWrapperItems>
        {getSectionsToRender(pageData).map((s) => (
          <ContentGrid key={s.id} sectionId={s.id} title={s.title}>
            <List gap="small">
              {s.id === SectionIdentifier.MUSIC &&
                releases.map((r, idx, self) => (
                  <SongLink
                    dsps={dspsMap[r.url?.replace('/', '') ?? ''] ?? []}
                    isMainPage
                    orderButtonsDisabledState={{
                      up: idx === 0,
                      down: idx === self.length - 1,
                    }}
                    key={r.id}
                    data={r}
                    artistName={pageData.title}
                  />
                ))}
              {s.id === SectionIdentifier.MERCH &&
                products.map((p, idx, self) => (
                  <ProductItemAlt
                    isMainPage
                    orderButtonsDisabledState={{
                      up: idx === 0,
                      down: idx === self.length - 1,
                    }}
                    key={p.id}
                    product={p}
                  />
                ))}
              {s.id === SectionIdentifier.EVENTS &&
                tourEvents.map((e, idx, self) => (
                  <TourItem
                    isMainPage
                    orderButtonsDisabledState={{
                      up: idx === 0,
                      down: idx === self.length - 1,
                    }}
                    key={e.id}
                    id={e.id ?? ''}
                    title={e.title}
                    location={e.place}
                    date={new Date(e.dateTime || '')}
                    href={e.link}
                  />
                ))}
            </List>
          </ContentGrid>
        ))}
      </HomeWrapperItems>
    </>
  );
};

export default connect(mapStateToProps)(AllPage);
